import { Component, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GlobalFunctions, imagePath, imgTypes, SnackType } from '@global/GlobalFunctions';
import { AuthenticationService } from '@services/authentication.service';
import { distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { AddAccountUserComponent } from '../add-account-user/add-account-user.component';
import { AssetDataPoint, DataPoint } from '@services/datapoints-data.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';

export class LinkAssetDatapointDialogData {
	datapoints: DataPoint[];
	hierarchy: OrganisationsHierarchy[];
	accountGUID: string;
	organisationUID: string;
}

@Component({
	selector: 'app-link-asset-datapoints',
	templateUrl: './link-asset-datapoints.component.html',
	styleUrls: ['./link-asset-datapoints.component.scss']
})
export class LinkAssetDatapointsComponent {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.accountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/** Form Group */
	dialogForm: FormGroup;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** datapoints */
	public datapoints: DataPoint[] = [];

	/** fields */
	public datapointFields: { text: string, value: string } = { text: "DPTitle", value: "DataPointID" };

	/** fields */
	public assetFields: { text: string, value: string } = { text: "Name", value: "AssetID" };

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** assets */
	public assets: LevelAsset[] = [];

	/** filteredAssets */
	public filteredAssets: LevelAsset[] = [];

	/** selectedAssets */
	public selectedAssets: LevelAsset[] = [];

	/** selectedDatapoints */
	public selectedDatapoints: DataPoint[] = [];

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/**
	 * Default Constructor
	 * @param fb fb
	 * @param dialogRef Referense to the Dialog box
	 * @param dialogData object interface
	 * @param authenticationService 
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<AddAccountUserComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: LinkAssetDatapointDialogData,
		private assetsDataService: AssetsDataService,
		private globalFunctions: GlobalFunctions
	) {
		this.datapoints = dialogData.datapoints;
		this.hierarchy = dialogData.hierarchy;
		this.accountGUID = dialogData.accountGUID;
		this.organisationUID = dialogData.organisationUID;
		this.assets = this.flattenAssets(this.hierarchy);
		this.filteredAssets = this.assets;
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.dialogForm = this.fb.group({
			// userID: [this.dialogData.userID || 0, Validators.required],
			// admin: [this.dialogData.admin || false, Validators.required]
		});
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * linkAssetDatapoints
	 */
	async linkAssetDatapoints(): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			if (this.selectedAssets.length > 0 && this.selectedDatapoints.length > 0) {
				// Create a local Subject to control the subscription lifecycle
				const fetchDataSubject = new Subject<void>();

				let assetsIDs: string = this.selectedAssets.map((x: LevelAsset) => x.AssetID).toString();
				let datapointIDs: string = this.selectedDatapoints.map((x: DataPoint) => x.DataPointID).toString();

				this.assetsDataService.putMultiAssetDataPoint$(this.accountGUID, this.organisationUID, assetsIDs, datapointIDs).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						this.globalFunctions.DisplaySnackBar({
							message: response.Data,
							type: SnackType.Info,
							duration: 2000
						});
						resolve(true);
					},
					error: (error) => {
						this.globalFunctions.HandleError(error, "Error: this.assetsDataService.putMultiAssetDataPoint$", false);
						reject(true);
					},
				});
			} else {
				this.globalFunctions.DisplaySnackBar({
					message: "Select at least one Asset and one DataPoint",
					type: SnackType.Info,
					duration: 2000
				});
				reject(true);
			}
		});
	}

	/**
	 * assetCheckboxChange
	 * @param event event 
	 * @param asset asset 
	 */
	assetCheckboxChange(event: any, asset: LevelAsset) {
		if (event.checked) {
			this.selectedAssets.push(asset);
		} else {
			this.selectedAssets = this.selectedAssets.filter((x: LevelAsset) => x.AssetID != asset.AssetID);
		}
	}

	/**
	 * datapointCheckboxChange
	 * @param event event 
	 * @param datapoint datapoint 
	 */
	datapointCheckboxChange(event: any, datapoint: DataPoint) {
		if (event.checked) {
			this.selectedDatapoints.push(datapoint);
		} else {
			this.selectedDatapoints = this.selectedDatapoints.filter((x: DataPoint) => x.DataPointID != datapoint.DataPointID);
		}
	}

	/**
	 * OKButtonClick
	 */
	async OKButtonClick() {
		try {
			await this.linkAssetDatapoints();
			this.dialogRef.close();
		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * onFilteringAssets
	 * @param event event 
	 */
	onFilteringAssets(event: any) {
		let input: string = event.target.value.toLowerCase();
		this.filteredAssets = this.assets.filter((x: LevelAsset) => {
			let name: string = x.Name;
			let description: string = x.Description;
			return `${name} - ${description}`.toLowerCase().includes(input);
		});
	}
}
